<template>
  <div>
    <b-table
      :data="movements"
      :loading="loading"
      :row-class="() => 'group'"
      :checked-rows.sync="selectedMovements"
      :is-row-checkable="(row) => row.status !== 'approved'"
      :checkable="visible('status')"
      :selectable="visible('status')"
      hoverable
      @select="selected"
      data-testid="bank-movements-table"
    >
      <b-table-column
        header-class="text-xs text-primary"
        field="unseenMovement"
        width="7"
        v-slot="props"
      >
        <div class="new-transaction-container">
          <div
            v-if="props.row.isNew"
            class="new-transaction"
          />
        </div>
      </b-table-column>
      <b-table-column
        header-class="text-xs text-primary"
        field="postDate"
        :label="columnFormatter($t('message.table.header.date'))"
        width="40"
        v-slot="props"
        :visible="visible('postDate')"
      >
        <p class="text-sm text-primary">
          {{ props.row.postDate | date(utc=true) }}
        </p>
      </b-table-column>
      <b-table-column
        header-class="text-xs text-primary"
        field="account"
        :label="columnFormatter($tc('message.table.header.account', 1))"
        width="40"
        :visible="visible('account')"
      >
        <template v-slot:header="{ column }">
          <div class="flex">
            {{ column.label }}

            <b-dropdown>
              <button
                class="my-auto ml-2"
                slot="trigger"
              >
                <inline-svg
                  :src="require('@assets/images/gear.svg')"
                  class="w-3 text-menu"
                />
              </button>
              <b-dropdown-item
                v-for="(name, key) in accountDisplays"
                :key="key"
              >
                <button
                  @click="setAccountDisplayType(key)"
                  class="text-xs text-primary"
                >
                  <div class="flex flex-row-reverse items-center justify-between w-full">
                    <p
                      :class="[accountDisplayType === key ? 'text-sc-blue' : 'text-primary',
                               'font-normal', 'text-sm', 'ml-2']"
                    >
                      {{ name }}
                    </p>
                    <inline-svg
                      v-if="accountDisplayType == key"
                      :src="require('@assets/images/blue-check.svg')"
                      class="w-3 text-sc-blue"
                    />
                  </div>
                </button>
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </template>

        <template v-slot="props">
          <p class="text-sm text-primary">
            {{ accountColumnText(props.row.accountType, props.row.accountNumber) }}
          </p>
          <p class="text-xs text-secondary">
            {{ accountColumnFooter(props.row.accountType, props.row.institution) }}
          </p>
        </template>
      </b-table-column>
      <b-table-column
        header-class="text-xs text-primary"
        field="emisor"
        :label="columnFormatter($t('message.table.header.issuing'))"
        width="60"
        v-slot="props"
        :visible="visible('emisor')"
      >
        <p class="text-sm text-primary">
          {{ props.row.senderName | textLimit(60) }}
        </p>
        <p class="text-xs text-secondary">
          {{ props.row.rut | rutFilter(false) }}
        </p>
      </b-table-column>
      <b-table-column
        header-class="text-xs text-primary"
        field="description"
        :label="columnFormatter($t('message.table.header.description'))"
        width="100"
        v-slot="props"
        :visible="visible('description')"
      >
        <span class="text-sm text-primary">
          {{ props.row.description | textLimit(80) }}
        </span>
      </b-table-column>
      <b-table-column
        header-class="text-xs text-primary"
        field="amount"
        :label="columnFormatter($t('message.table.header.inbound'))"
        width="35"
        numeric
        v-slot="props"
        :visible="visible('amount')"
      >
        <div
          class="flex justify-end text-sm text-primary"
          v-if="props.row.amount > 0"
        >
          {{ formattedValue(props.row) }}
          <img
            :src="require('@assets/images/green_arrow.svg')"
            class="self-center h-3 p-1 ml-1 transform rotate-180 bg-approved"
            alt="flecha para abajo"
          >
        </div>
      </b-table-column>
      <b-table-column
        header-class="text-xs text-primary"
        field="amount"
        :label="columnFormatter($t('message.table.header.outbound'))"
        width="15"
        numeric
        v-slot="props"
        :visible="visible('amount')"
      >
        <div
          class="flex justify-end text-sm text-primary"
          v-if="props.row.amount < 0"
        >
          {{ formattedValue(props.row) }}
          <img
            :src="require('@assets/images/red_arrow.svg')"
            class="self-center h-3 p-1 ml-1 transform rotate-180 bg-rejected"
            alt="flecha para arriba"
          >
        </div>
      </b-table-column>
      <b-table-column
        header-class="text-xs text-primary"
        field="status"
        :label="columnFormatter($tc('message.table.header.status', 1))"
        width="15"
        v-slot="props"
        :visible="visible('status')"
      >
        <span
          :class="[props.row.status === 'approved' ? 'bg-approved' : 'bg-pending cursor-pointer',
                   'text-xs', 'text-primary']"
          class="p-1 rounded"
        >
          <button
            @click="confirmAction(props.row)"
          >
            {{ translateStatus[props.row.status] }}
          </button>
        </span>
      </b-table-column>

      <b-table-column
        v-for="name in extraColumnsNames"
        header-class="w-40 text-xs text-primary group"
        :key="name"
        :label="columnFormatter(name)"
        centered
      >
        <template
          v-slot:header="{ column }"
        >
          <div class="flex">
            {{ column.label }}
            <button
              v-if="isAdmin"
              @click="showExtraColumnModal(name)"
              class="visible w-4 h-4 ml-2 rounded md:invisible shadow-sc-button group-hover:visible"
            >
              <img
                class="h-4"
                :src="require('@assets/images/edit.svg')"
              >
            </button>
          </div>
        </template>
        <template v-slot="props">
          <b-dropdown
            v-if="extraColumnsSchema[name].type == 'Enum'"
            :append-to-body="true"
          >
            <b-tag
              slot="trigger"
              type="is-primary"
              rounded
            >
              <div
                class="flex"
              >
                <span>{{ extraColumnsData[props.row.movementId][name] }}</span>
                <img
                  class="px-2"
                  :src="require('@assets/images/chevron-down.svg')"
                >
              </div>
            </b-tag>
            <b-dropdown-item
              v-for="(tag, key) in extraColumnsSchema[name].options.tags"
              :key="key"
              class="px-0"
            >
              <button
                class="w-full pl-2 text-left"
                @click="assignExtraColumnData(props.row, name, tag)"
              >
                {{ tag }}
              </button>
            </b-dropdown-item>
          </b-dropdown>
          <div
            v-else-if="extraColumnsSchema[name].type == 'String'"
            class="flex justify-center w-full min-w-40"
          >
            <component
              :is="extraColumnsComponents[extraColumnsSchema[name].type]"
              :key="props.row.id"
              :value="extraColumnsData[props.row.movementId][name]"
              @blur="sendUpdate(props.row, name, $event.target.value)"
              @focus="focusedRowId = props.row.id"
              :icon-right="showIcon(props.row.id, props.row.movementId, name) ? 'content-save-outline' : ''"
              icon-right-clickable
              @icon-right-click="sendUpdate(props.row, name, $event.target.value)"
              size="is-small"
              class="justify-center w-full custom-column"
              @keyup.enter.native="$event.target.blur()"
            />
            <div
              class="border-b-2 border-sc-blue"
              v-if="isLoading(props.row.movementId, name)"
            >
              <img
                :src="require('@assets/images/loading.svg')"
                class="z-10 px-1 my-auto w-12 py0.5"
              >
            </div>
          </div>
          <div
            v-else
            class="flex justify-center min-w-full"
          >
            <component
              :is="extraColumnsComponents[extraColumnsSchema[name].type]"
              v-model="extraColumnsData[props.row.movementId][name]"
              @input="updateExtraColumnData(props.row)"
              :append-to-body="true"
              :date-formatter="dateFormatter"
              :focused-date="focusedDate(extraColumnsData[props.row.movementId][name])"
              size="is-small"
              class="justify-center w-24 min-w-full custom-column"
            />
          </div>
        </template>
      </b-table-column>
      <b-table-column
        header-class="text-xs text-primary"
        :label="columnFormatter($t('message.table.header.action'))"
        width="100"
        centered
      >
        <template v-slot:header="{ column }">
          <div class="flex">
            {{ column.label }}
            <button
              v-if="isAdmin"
              class="flex justify-center items-center ml-3.5 w-4 h-4 rounded shadow-sc-button"
              @click="showExtraColumnModal(null)"
            >
              +
            </button>
          </div>
        </template>
        <template v-slot="props">
          <button
            @click="viewMovement(props.row)"
            class="visible my-auto bg-white rounded md:invisible shadow-sc-button text-primary group-hover:visible"
          >
            <span class="px-2 py-1 text-xs">{{ $t('message.table.button.view') }}</span>
          </button>
        </template>
      </b-table-column>
      <b-table-column
        field="show"
        v-slot="props"
        width="20"
        :visible="visible('detail')"
      >
        <div
          class="relative w-5"
          v-if="props.row.commentCount > 0"
        >
          <img
            :src="require('@assets/images/comment.svg')"
            class="inset-0 w-full m-auto opacity-50"
            alt="comentarios"
          >
          <span class="absolute inset-0 m-auto text-center text-2xs text-primary">
            {{ props.row.commentCount }}
          </span>
        </div>
      </b-table-column>
      <template v-slot:empty>
        <div class="text-center">
          <p>{{ $t('message.table.emptyMessage') }}</p>
          <small>{{ $t('message.table.loadingTimeMessage') }}</small>
        </div>
      </template>
    </b-table>
    <approve-confirmation-modal
      name="confirmationModalMovementsTable"
      ref="confirmationModalMovementsTable"
      :step="'single'"
      @confirmation="approveMovement"
    />
    <bank-movement-view-modal
      ref="viewModal"
      :movement-id="selectedMovementId"
      :entity-id="parseInt(entityId, 10)"
    />
    <modal
      ref="extra-column-modal"
      name="extra-column-modal"
      height="auto"
      :focus-trap="false"
      classes="overflow-visible"
    >
      <extra-columns-modal
        @close="closeExtraColumnModal"
        :entity-configuration="entityConfiguration"
        :selected-extra-column="selectedExtraColumn"
      />
    </modal>
  </div>
</template>
<script>
import { camelize } from 'humps';
import currency from '../../filters/currency.js';
import searchDebounce from '../../utils/searchDebounce';
import isMobile from '../../utils/isMobile';
import BankMovementViewModal from './bank-movement-view-modal.vue';
import ApproveConfirmationModal from '../approve-confirmation-modal.vue';
import movementApi from '../../api/bank-movements.js';
import DefaultDict from '../../values/default-dict.js';
import date from '../../filters/date';
import ExtraColumnsModal from '../extra-columns-modal.vue';
import columnFormatter from '../../../helpers/columnFormatter';

const DEBOUNCE_TIME = 1000;

export default {
  data() {
    return {
      selectedMovement: null,
      selectedExtraColumn: null,
      data: [],
      selectedMovements: [],
      accountDisplayType: this.getAccountDisplayType(),
      accountDisplays: {
        'custom': this.$t('message.accounts.displayMode.custom'),
        'number': this.$t('message.accounts.displayMode.number'),
      },
      translateStatus: {
        'pending': this.$t('message.table.status.pending'),
        'approved': this.$t('message.table.status.approved'),
      },
      extraColumnsData: new DefaultDict(Object),
      extraColumnsComponents: {
        'Boolean': 'b-checkbox',
        'Date': 'b-datepicker',
        'String': 'b-input',
      },
      debouncedUpdateExtraColumnData: searchDebounce(function (movement) {
        this.updateExtraColumnData(movement);
      }, DEBOUNCE_TIME),
      debouncedRowId: null,
      debouncedRowName: null,
      focusedRowId: null,
      errors: [],
      isMobile: isMobile(),
    };
  },
  components: {
    BankMovementViewModal,
    ApproveConfirmationModal,
    ExtraColumnsModal,
  },
  props: {
    showPlusButton: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    movements() {
      const newMovements = new Set(this.$store.state.movements.unseenMovements);

      return this.$store.state.movements.movements.map(movement => (
        {
          postDate: movement.postDate,
          rut: movement.senderRut ? movement.senderRut || '-' : '-',
          senderName: movement.senderName ? movement.senderName || '-' : '-',
          description: movement.description,
          amount: movement.amount,
          currency: movement.currency,
          recipientName: movement.recipientName ? movement.recipientName || '-' : '-',
          movementId: movement.id,
          status: movement.status,
          institution: movement.bankAccount.institution,
          accountType: movement.bankAccount.customName,
          accountNumber: movement.bankAccount.number,
          extraColumns: this.indexExtraColumns(movement.defaultedExtraColumns),
          commentCount: movement.commentCount,
          isNew: newMovements.has(movement.id),
          id: movement.id,
        }
      ));
    },
    loading() {
      return this.$store.state.movements.loading;
    },
    entityId() {
      return this.$store.state.entities.entityId;
    },
    selectedMovementId() {
      return this.selectedMovement && this.selectedMovement.movementId;
    },
    extraColumnsSchema() {
      const configuration = this.entityConfiguration;
      const extraColumnsSchemaArray = configuration && configuration.extraColumnsSchemaArray;
      if (!extraColumnsSchemaArray) return {};

      const schema = {};
      extraColumnsSchemaArray.forEach((columnSchema) => {
        schema[columnSchema.name] = columnSchema;
      });

      return schema;
    },
    extraColumnsNames() {
      return this.extraColumnsSchema && Object.values(this.extraColumnsSchema).map(col => col.name);
    },
    entityConfiguration() {
      return this.$store.state.entities.entityConfiguration;
    },
    isAdmin() {
      return this.$store.state.users.isAdmin;
    },
  },
  methods: {
    formattedValue(row) {
      const formattedValue = row.amount && currency(row.amount, row.currency);

      return formattedValue > 0 ? formattedValue : formattedValue.replace('-', '');
    },
    clearSelectedMovements() {
      this.selectedMovements = [];
    },
    showExtraColumnModal(extraColumnName) {
      this.selectedExtraColumn = this.entityConfiguration.extraColumnsSchemaArray.find(
        (column) => column.name === extraColumnName);
      this.$modal.show('extra-column-modal');
    },
    async closeExtraColumnModal() {
      // this.reloadConfiguration();
      this.selectedExtraColumn = null;
      this.$store.dispatch('getEntityConfiguration');
      this.$store.dispatch('getMovements');
      this.$modal.hide('extra-column-modal');
    },
    accountColumnText(accountType, accountNumber) {
      const SLICE_AMOUNT = -4;

      return this.accountDisplayType === 'custom' ?
        (accountType || `XX${accountNumber.slice(SLICE_AMOUNT)}`) :
        `XX${accountNumber.slice(SLICE_AMOUNT)}`;
    },
    accountColumnFooter(accountType, institution) {
      return this.accountDisplayType === 'custom' ?
        this.$t(`message.accounts.institution.shortNames.${camelize(institution)}`) :
        accountType || this.$t(`message.accounts.institution.shortNames.${camelize(institution)}`);
    },
    camelize(string) {
      return camelize(string);
    },
    columnFormatter,
    dateFormatter(dateValue) {
      const unixTime = dateValue.getTime();

      return unixTime ? date(dateValue) : '';
    },
    focusedDate(dateString) {
      const dateValue = new Date(dateString);

      return dateValue.getTime() > 0 ? dateValue : new Date();
    },
    indexExtraColumns(columnArray) {
      const indexedExtraColumns = {};
      columnArray.forEach(column => {
        indexedExtraColumns[column.name] = column;
      });

      return indexedExtraColumns;
    },
    viewMovement(movement) {
      this.selectedMovement = movement;
      this.toggleViewModal();
    },
    visible(columnName) {
      return this.$store.getters.visibleColumn(columnName);
    },
    confirmAction(movement) {
      if (movement.status !== 'approved') {
        this.selectedMovement = movement;
        this.toggleConfirmationModal();
      }
    },
    async approveMovement(content = '') {
      this.$store.dispatch('approveMovement', { movementId: this.selectedMovementId, content });
    },
    toggleConfirmationModal() {
      this.$refs.confirmationModalMovementsTable.show();
    },
    toggleViewModal() {
      this.$refs.viewModal.show();
    },
    getAccountDisplayType() {
      return this.$cookies.get('accountDisplayType') || 'number';
    },
    setAccountDisplayType(key) {
      this.accountDisplayType = key;
      this.$cookies.set('accountDisplayType', key, Infinity);
    },
    async updateBankMovement(movement) {
      try {
        const { movementId } = movement;
        const extraColumns = [];
        this.error = false;
        this.extraColumnsNames.forEach(name => {
          extraColumns.push({ 'name': name, 'value': this.extraColumnsData[movementId][name] });
        });
        await movementApi.updateExtraColumns({ extraColumns }, { movementId, entityId: this.entityId });
        this.debouncedRowId = null;
        this.debouncedRowName = null;
        this.$store.dispatch('getMovement', movementId).then((bankMovement) => {
          this.$store.dispatch('updateMovement', bankMovement);
        });
      } catch (error) {
        this.errors.push(error.message);
      }
    },
    updateExtraColumnData(movement) {
      this.updateBankMovement(movement);
    },
    assignExtraColumnData(movement, name, data) {
      this.extraColumnsData[movement.movementId][name] = data;
      this.updateExtraColumnData(movement);
    },
    selected(item) {
      this.$store.dispatch('removeSeenMovement', item);
      this.handleMovementSelection(item);
    },
    handleMovementSelection(item) {
      if (item.status === 'approved') return;
      if (this.selectedMovements.includes(item)) {
        this.selectedMovements.splice(this.selectedMovements.indexOf(item), 1);
      } else {
        this.selectedMovements.push(item);
      }
    },
    debounceNow() {
      this.debouncedUpdateExtraColumnData.flush();
    },
    sendUpdate(movement, name, value) {
      this.debounceNow();
      this.extraColumnsData[movement.id][name] = value;
      this.$store.dispatch('updateMovementDefaultedExtraColumn', { id: movement.id, name, value });
      this.debouncedRowId = movement.id;
      this.debouncedRowName = name;
      this.debouncedUpdateExtraColumnData(movement);
      this.focusedRowId = null;
    },
    isLoading(id, name) {
      return this.debouncedRowId === id && this.debouncedRowName === name;
    },
    showIcon(rowId, rowMovementId, name) {
      return this.focusedRowId === rowId && !this.isLoading(rowMovementId, name);
    },
  },
  watch: {
    selectedMovements() {
      this.$store.dispatch('refreshSelectedMovements', this.selectedMovements);
    },
    movements(newMovements) {
      newMovements.forEach(movement => {
        const id = movement.movementId;
        this.extraColumnsNames.forEach(name => {
          let columnValue = movement.extraColumns[name].value;
          if (this.extraColumnsSchema[name].type === 'Date') {
            columnValue = new Date(columnValue);
          }
          this.extraColumnsData[id][name] = columnValue;
        });
      });
    },
  },
};
</script>
<style lang="scss">
.b-table {
  .table {
    td {
      vertical-align: middle;
    }
  }
}

.custom-column {
  .is-small.input {
    @apply border-0 border-b-2 border-sc-gray-light shadow-none bg-transparent rounded-none pl-0;
    @apply focus:border-sc-blue;
  }
}

.new-transaction-container {
  width: 10px;
  height: 10px;
}

.new-transaction {
  border-radius: 50%;
  width: 7px;
  height: 7px;
  @apply bg-sc-blue;
  animation: breathe 2s ease-in-out;
  animation-iteration-count: infinite;
}

@keyframes breathe {
  0% {
    opacity: .5;
  }

  40% {
    opacity: 1;
  }

  60% {
    opacity: 1;
  }

  100% {
    opacity: .5;
  }
}
</style>
